import { NetworkStatus, useQuery } from "@apollo/client";
import {
  Button,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  DataTable,
  DataTableColumnHeader,
  DateWithDelta,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  StatusUI,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { prettyPrintEnum } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { type ReactNode, Suspense, useMemo, useState } from "react";
import {
  RiBuildingLine,
  RiFilter3Line,
  RiListCheck,
  RiPriceTagLine,
} from "react-icons/ri";
import { z } from "zod";
import { TransactionMonitoringEvaluationTargetCard } from "~/components/TransactionMonitoring/TransactionMonitoringEvaluationTargetCard";
import { graphql } from "~/gql/client";
import {
  transactionMonitoringEvaluationFilterTargetKindValues,
  transactionMonitoringEvaluationRuleSetValues,
  transactionMonitoringEvaluationStatusValues,
} from "~/gql/client/enumValues";
import type {
  AdminTransactionMonitoringEvaluationRowFragment,
  TransactionMonitoringEvaluationFilter,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const adminTransactionMonitoringEvaluationsDocument = graphql(`
  query AdminTransactionMonitoringEvaluations(
    $after: String
    $first: Int!
    $filter: TransactionMonitoringEvaluationFilter!
  ) {
    organisations {
      id
      name
    }
    transactionMonitoringEvaluations(
      after: $after
      first: $first
      filter: $filter
    ) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
      }
      transactionMonitoringEvaluations {
        ...AdminTransactionMonitoringEvaluationRow
      }
    }
  }

  fragment AdminTransactionMonitoringEvaluationRow on TransactionMonitoringEvaluation {
    id
    shortRef
    createdAt
    ruleSet
    status
    target {
      ...TransactionMonitoringEvaluationTargetCardFields
    }
    findings {
      id
    }
  }
`);

const evaluationsFilterSchema = z.object({
  organisationIds: z.array(z.string()).optional().nullable(),
  statuses: z
    .array(z.enum(transactionMonitoringEvaluationStatusValues))
    .optional()
    .nullable(),
  ruleSets: z
    .array(z.enum(transactionMonitoringEvaluationRuleSetValues))
    .optional()
    .nullable(),
  targetKinds: z
    .array(z.enum(transactionMonitoringEvaluationFilterTargetKindValues))
    .optional()
    .nullable(),
});

export const Route = createFileRoute(
  "/__auth/transaction-monitoring/evaluations",
)({
  head: () => opsHead("Transaction Monitoring Evaluations"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const columns: ColumnDef<AdminTransactionMonitoringEvaluationRowFragment>[] = [
  {
    id: "createdAt",
    accessorFn: (row) => row.createdAt,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => <DateWithDelta date={row.original.createdAt} />,
    enableSorting: false,
  },
  {
    id: "ruleSet",
    accessorFn: (row) => row.ruleSet,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Rule set" />
    ),
    cell: ({ row }) => prettyPrintEnum(row.original.ruleSet),
    enableSorting: false,
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => <StatusUI status={row.original.status} />,
    enableSorting: false,
  },
  {
    id: "target",
    accessorFn: (row) => row.target?.id ?? row.id,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Target" />
    ),
    cell: ({ row }) => {
      if (!row.original.target) {
        return "-";
      }

      return (
        <TransactionMonitoringEvaluationTargetCard
          target={row.original.target}
        />
      );
    },
    enableSorting: false,
  },
  {
    id: "findingCount",
    accessorFn: (row) => row.findings?.length ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Finding count" />
    ),
    cell: ({ row }) => row.original.findings?.length ?? 0,
    enableSorting: false,
  },
];

function View() {
  const navigate = useNavigate();
  const { useSearchParams } = useRouter();

  const [filter, setFilter] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: evaluationsFilterSchema,
    defaultState: {
      organisationIds: undefined,
      statuses: undefined,
      ruleSets: undefined,
      targetKinds: undefined,
    },
  });

  const queryFilter: TransactionMonitoringEvaluationFilter = {
    organisationIds: filter.organisationIds ?? undefined,
    statuses: filter.statuses ?? undefined,
    ruleSets: filter.ruleSets ?? undefined,
    targetKinds: filter.targetKinds ?? undefined,
  };

  const baseInput: {
    filter: TransactionMonitoringEvaluationFilter;
    first: number;
  } = {
    first: 20,
    filter: queryFilter,
  };

  const { data, fetchMore, loading, networkStatus } = useQuery(
    adminTransactionMonitoringEvaluationsDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const organisationOptions = useMemo(
    () =>
      [...(data?.organisations ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((organisation) => ({
          value: organisation.id,
          label: <div>{organisation.name}</div>,
          searchValues: [organisation.name],
        })),
    [data?.organisations],
  );

  const evaluations =
    data?.transactionMonitoringEvaluations?.transactionMonitoringEvaluations ??
    [];
  const nextCursor = data?.transactionMonitoringEvaluations?.pageInfo
    .hasNextPage
    ? (data.transactionMonitoringEvaluations.pageInfo.endCursor ?? undefined)
    : undefined;

  return (
    <div>
      <EvaluationsFilterBar
        filterValue={filter}
        organisationOptions={organisationOptions}
        onFilterChange={setFilter}
        totalCount={data?.transactionMonitoringEvaluations?.totalCount ?? 0}
      />
      <DataTable
        data={evaluations}
        columns={columns}
        isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
        manualPagination={true}
        hidePagination={true}
        pageSize={10_000}
        className="overflow-hidden border"
        onRowClick={(row) => {
          navigate({
            to: "/transaction-monitoring/evaluations/$evaluationId",
            params: { evaluationId: row.original.id },
          });
        }}
      />
      <div className="mt-4 flex w-full flex-row justify-center">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({ variables: { ...baseInput, after: nextCursor } })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!evaluations.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>
    </div>
  );
}

function EvaluationsFilterBar({
  filterValue,
  organisationOptions,
  onFilterChange,
  totalCount,
}: {
  filterValue: z.infer<typeof evaluationsFilterSchema>;
  organisationOptions: Array<{
    value: string;
    label: ReactNode;
    searchValues: string[];
  }>;
  onFilterChange: (_: z.infer<typeof evaluationsFilterSchema>) => void;
  totalCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("status");

  const statusOptions = useMemo(
    () =>
      transactionMonitoringEvaluationStatusValues.map((value) => ({
        value,
        label: prettyPrintEnum(value),
      })),
    [],
  );

  const ruleSetOptions = useMemo(
    () =>
      transactionMonitoringEvaluationRuleSetValues.map((value) => ({
        value,
        label: prettyPrintEnum(value),
      })),
    [],
  );

  const targetKindOptions = useMemo(
    () =>
      transactionMonitoringEvaluationFilterTargetKindValues.map((value) => ({
        value,
        label: prettyPrintEnum(value),
      })),
    [],
  );

  const filterCount = [
    filterValue.organisationIds?.length ?? 0,
    filterValue.statuses?.length ?? 0,
    filterValue.ruleSets?.length ?? 0,
    filterValue.targetKinds?.length ?? 0,
  ].filter((count) => count > 0).length;

  const resetFilter = () => {
    onFilterChange({
      organisationIds: undefined,
      statuses: undefined,
      ruleSets: undefined,
      targetKinds: undefined,
    });
  };

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Popover open={open} onOpenChange={(nextOpen) => setOpen(nextOpen)}>
          <PopoverTrigger asChild>
            <Button
              leftIcon={<RiFilter3Line />}
              variant="outline"
              size="sm"
              className="h-8 w-fit text-primary"
            >
              Filter
              {filterCount > 0 ? (
                <span className="ml-2 text-info">({filterCount})</span>
              ) : null}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="flex h-[500px] w-[760px] p-0"
            align="start"
          >
            <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
              <div className="flex flex-col gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 text-primary"
                  onClick={() => setPath("organisation")}
                >
                  <RiBuildingLine />
                  <div className="flex flex-1">Organisation</div>
                  {(filterValue.organisationIds?.length ?? 0) > 0 && (
                    <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 text-primary"
                  onClick={() => setPath("status")}
                >
                  <RiPriceTagLine />
                  <div className="flex flex-1">Status</div>
                  {(filterValue.statuses?.length ?? 0) > 0 && (
                    <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 text-primary"
                  onClick={() => setPath("targetKind")}
                >
                  <RiListCheck />
                  <div className="flex flex-1">Target kind</div>
                  {(filterValue.targetKinds?.length ?? 0) > 0 && (
                    <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 text-primary"
                  onClick={() => setPath("ruleSet")}
                >
                  <RiListCheck />
                  <div className="flex flex-1">Rule set</div>
                  {(filterValue.ruleSets?.length ?? 0) > 0 && (
                    <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                  )}
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 bg-primary text-primary"
                onClick={resetFilter}
              >
                Reset all filters
              </Button>
            </div>
            <div className="flex-1">
              {path === "organisation" && (
                <ControlledFacetedFilterControl
                  options={organisationOptions}
                  selectedValues={filterValue.organisationIds ?? []}
                  onChange={(organisationIds) =>
                    onFilterChange({
                      ...filterValue,
                      organisationIds:
                        organisationIds.length > 0
                          ? (organisationIds as typeof filterValue.organisationIds)
                          : undefined,
                    })
                  }
                />
              )}
              {path === "status" && (
                <ControlledFacetedFilterControl
                  options={statusOptions}
                  selectedValues={filterValue.statuses ?? []}
                  onChange={(statuses) =>
                    onFilterChange({
                      ...filterValue,
                      statuses:
                        statuses.length > 0
                          ? (statuses as typeof filterValue.statuses)
                          : undefined,
                    })
                  }
                />
              )}
              {path === "targetKind" && (
                <ControlledFacetedFilterControl
                  options={targetKindOptions}
                  selectedValues={filterValue.targetKinds ?? []}
                  onChange={(targetKinds) =>
                    onFilterChange({
                      ...filterValue,
                      targetKinds:
                        targetKinds.length > 0
                          ? (targetKinds as typeof filterValue.targetKinds)
                          : undefined,
                    })
                  }
                />
              )}
              {path === "ruleSet" && (
                <ControlledFacetedFilterControl
                  options={ruleSetOptions}
                  selectedValues={filterValue.ruleSets ?? []}
                  onChange={(ruleSets) =>
                    onFilterChange({
                      ...filterValue,
                      ruleSets:
                        ruleSets.length > 0
                          ? (ruleSets as typeof filterValue.ruleSets)
                          : undefined,
                    })
                  }
                />
              )}
            </div>
          </PopoverContent>
        </Popover>
        <ControlledFactedFilterSummary
          title="Organisation"
          options={organisationOptions}
          selectedValues={filterValue.organisationIds ?? []}
          onClick={() => {
            setPath("organisation");
            setOpen(true);
          }}
        />
        <ControlledFactedFilterSummary
          title="Status"
          options={statusOptions}
          selectedValues={filterValue.statuses ?? []}
          onClick={() => {
            setPath("status");
            setOpen(true);
          }}
        />
        <ControlledFactedFilterSummary
          title="Target kind"
          options={targetKindOptions}
          selectedValues={filterValue.targetKinds ?? []}
          onClick={() => {
            setPath("targetKind");
            setOpen(true);
          }}
        />
        <ControlledFactedFilterSummary
          title="Rule set"
          options={ruleSetOptions}
          selectedValues={filterValue.ruleSets ?? []}
          onClick={() => {
            setPath("ruleSet");
            setOpen(true);
          }}
        />
        {filterCount > 0 && (
          <Button
            rightIcon={<X />}
            variant="ghost"
            size="sm"
            onClick={resetFilter}
          >
            Reset
          </Button>
        )}
      </div>
      <div className="text-secondary text-sm">
        {totalCount === 1 ? "1 evaluation" : `${totalCount} evaluations`}
      </div>
    </div>
  );
}
